import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLang } from "@/i18n/LanguageContext";
import { ALL_CATS, catPath, homePath, contractorPath, blogPath, altPath } from "@/i18n/slugs";
import { FAMILIES } from "@/i18n/taxonomy";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, T } = useLang();

  const scrollToForm = () => {
    const formElement = document.getElementById("lead-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `${homePath(lang)}#lead-form`;
    }
  };

  const LangSwitcher = ({ className = "" }) => (
    <div className={`flex items-center gap-1 text-sm font-medium ${className}`} data-testid="lang-switcher">
      <Globe className="w-4 h-4 text-slate-400 mr-1" />
      <Link
        to={altPath(location.pathname, "fr")}
        className={`px-1.5 py-0.5 rounded ${lang === "fr" ? "text-orange-500 font-bold" : "text-slate-500 hover:text-slate-900"}`}
        data-testid="lang-fr-link"
      >
        FR
      </Link>
      <span className="text-slate-300">|</span>
      <Link
        to={altPath(location.pathname, "nl")}
        className={`px-1.5 py-0.5 rounded ${lang === "nl" ? "text-orange-500 font-bold" : "text-slate-500 hover:text-slate-900"}`}
        data-testid="lang-nl-link"
      >
        NL
      </Link>
    </div>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={homePath(lang)} className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">DM</span>
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">
              DevisMaison<span className="text-orange-500">.be</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid="categories-dropdown">
                {T.nav.works} <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[680px] max-h-[70vh] overflow-y-auto p-4">
                <div className="grid grid-cols-3 gap-x-4 gap-y-5">
                  {FAMILIES.map((f) => (
                    <div key={f.key}>
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-2 pb-1">
                        {f.icon} {lang === "nl" ? f.nl : f.fr}
                      </p>
                      {ALL_CATS.filter((c) => c.family === f.key).map((cat) => (
                        <a
                          key={cat.key}
                          href={catPath(cat.key, lang)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-2 py-1 text-sm text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded"
                          data-testid={`nav-devis-${cat.key}`}
                        >
                          {cat[lang].nav}
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to={blogPath(lang)}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-blog"
            >
              {T.nav.blog}
            </Link>

            <Link
              to={contractorPath(lang)}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-entrepreneur"
            >
              {T.nav.partner}
            </Link>

            <Link
              to="/espace-entrepreneur/login"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-espace-client"
            >
              {T.nav.myspace}
            </Link>

            <LangSwitcher />

            <Button
              onClick={scrollToForm}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2 rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/25"
              data-testid="cta-devis-button"
            >
              {T.nav.cta}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <LangSwitcher />
            <button
              className="p-2 text-slate-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              <div className="px-3 pb-1">
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    scrollToForm();
                  }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full"
                  data-testid="mobile-cta-devis"
                >
                  {T.nav.cta}
                </Button>
              </div>
              <Link
                to={contractorPath(lang)}
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-entrepreneur"
              >
                {T.nav.partner}
              </Link>
              <Link
                to="/espace-entrepreneur/login"
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-espace"
              >
                {T.nav.myspace}
              </Link>
              <Link
                to={blogPath(lang)}
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-blog"
              >
                {T.nav.blog}
              </Link>
              <div className="border-t border-slate-200 my-2" />
              <p className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {T.nav.works}
              </p>
              {FAMILIES.map((f) => (
                <details key={f.key} className="px-1">
                  <summary className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg cursor-pointer list-none flex items-center gap-2">
                    <span>{f.icon}</span> {lang === "nl" ? f.nl : f.fr}
                  </summary>
                  {ALL_CATS.filter((c) => c.family === f.key).map((cat) => (
                    <a
                      key={cat.key}
                      href={catPath(cat.key, lang)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-8 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg block"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {cat[lang].nav}
                    </a>
                  ))}
                </details>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
